import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Filter, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { JobCategory } from '../api/jobTypesApi';
import type { WorkOrderStatusItem } from '../api/workOrderStatusApi';
import { useWorkOrderJobTypes } from '../hooks/useWorkOrderJobTypes';
import { useWorkOrderPriorities } from '../hooks/useWorkOrderPriorities';
import { useWorkOrderStatusList } from '../hooks/useWorkOrderStatusList';

interface WorkOrdersFilterDropdownsProps {
  isOpen: boolean;
  onClose: () => void;
  priorityFilter: string;
  jobTypeFilter: string;
  workOrderStatusFilter: string;
  mirPendingDirectionFilter: string;
  onApply: (values: { priority: string; jobType: string; status: string; mir: string }) => void;
  onClear: () => void;
}

export function WorkOrdersFilterDropdowns({
  isOpen,
  onClose,
  priorityFilter,
  jobTypeFilter,
  workOrderStatusFilter,
  mirPendingDirectionFilter,
  onApply,
  onClear,
}: WorkOrdersFilterDropdownsProps) {
  const { data: jobTypesData, isLoading: jobTypesLoading } = useWorkOrderJobTypes();
  const { data: prioritiesData, isLoading: prioritiesLoading } = useWorkOrderPriorities();
  const { data: workOrderStatusData, isLoading: workOrderStatusLoading } = useWorkOrderStatusList();

  const [draftPriority, setDraftPriority] = useState(priorityFilter);
  const [draftJobType, setDraftJobType] = useState(jobTypeFilter);
  const [draftStatus, setDraftStatus] = useState(workOrderStatusFilter);
  const [draftMir, setDraftMir] = useState(mirPendingDirectionFilter);

  // Sync draft values with committed values each time the panel opens
  useEffect(() => {
    if (isOpen) {
      setDraftPriority(priorityFilter);
      setDraftJobType(jobTypeFilter);
      setDraftStatus(workOrderStatusFilter);
      setDraftMir(mirPendingDirectionFilter);
    }
  }, [isOpen, priorityFilter, jobTypeFilter, workOrderStatusFilter, mirPendingDirectionFilter]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close filter panel"
      />

      {/* Slide-in panel */}
      <div className="fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-foreground">Jobs Filters</span>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Filter content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Priority */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Priority
            </div>
            <Select
              value={draftPriority}
              onValueChange={setDraftPriority}
              disabled={prioritiesLoading}
            >
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder={prioritiesLoading ? 'Loading...' : 'Priority'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {(prioritiesData ?? []).map(({ id, name }) => (
                  <SelectItem key={id} value={String(id)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Job Type */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Job Type
            </div>
            <Select value={draftJobType} onValueChange={setDraftJobType} disabled={jobTypesLoading}>
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder={jobTypesLoading ? 'Loading...' : 'Job Type'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Job Types</SelectItem>
                {(jobTypesData ?? []).map((jobType: JobCategory) => (
                  <SelectItem key={jobType.id} value={String(jobType.id)}>
                    {jobType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Job Status
            </div>
            <Select
              value={draftStatus}
              onValueChange={setDraftStatus}
              disabled={workOrderStatusLoading}
            >
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder={workOrderStatusLoading ? 'Loading...' : 'Job Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workflow Statuses</SelectItem>
                {(workOrderStatusData ?? []).map((status: WorkOrderStatusItem) => (
                  <SelectItem key={status.id} value={String(status.id)}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Info Request */}
          <div>
            <div className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Info Request
            </div>
            <Select value={draftMir} onValueChange={setDraftMir}>
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder="Info Request" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Info Requests</SelectItem>
                <SelectItem value="inbound">Reply Needed</SelectItem>
                <SelectItem value="outbound">Awaiting Reply</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1 border-border" onClick={onClear}>
            Clear All
          </Button>
          <Button
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() =>
              onApply({
                priority: draftPriority,
                jobType: draftJobType,
                status: draftStatus,
                mir: draftMir,
              })
            }
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </>
  );
}
